import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Icon from "../../../components/AppIcon";

const BookingCTA = () => {
  const [scrollY, setScrollY] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [timeLeft, setTimeLeft] = useState("");

  // Scroll handler for background
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Realtime countdown starting at beginning of month, resets every month
  useEffect(() => {
    const getExpiration = () => {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const expiration = new Date(startOfMonth);
      expiration.setDate(startOfMonth.getDate() + 14); // 2 weeks from start of month
      return expiration;
    };

    const updateTime = () => {
      const now = new Date();
      let expiration = getExpiration();

      // If the current date is past the 2 weeks, move to next month
      if (now > expiration) {
        const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        expiration = new Date(nextMonth);
        expiration.setDate(nextMonth.getDate() + 14);
      }

      const diff = expiration - now;

      if (diff <= 0) {
        setTimeLeft("Expired");
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);

      setTimeLeft(`${days}d ${hours}h ${minutes}m ${seconds}s`);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const benefits = [
    {
      icon: "Calendar",
      title: "Free Consultation",
      description:
        "Complimentary 30-minute strategy session to discuss your vision",
    },
    {
      icon: "FileText",
      title: "Custom Proposal",
      description:
        "Tailored project scope and timeline based on your specific needs",
    },
    {
      icon: "Shield",
      title: "Risk-Free Planning",
      description: "No commitment required during initial consultation phase",
    },
  ];

  return (
    <section data-theme="light" className="py-4 sm:py-8 md:py-12 bg-background bg-background relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div
          className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-accent/5 to-transparent"
          style={{ transform: `translateX(${scrollY * 0.1}px)` }}
        />
        <div
          className="absolute bottom-0 left-0 w-1/4 h-1/2 bg-gradient-to-r from-primary/5 to-transparent"
          style={{ transform: `translateX(${scrollY * -0.1}px)` }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-xl">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary mb-2 sm:mb-4">
            Ready to Elevate Your
            <span className="text-gradient-gold"> Brand Story</span>?
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <div className="relative bg-gradient-to-br from-primary via-red-900 to-secondary rounded-2xl p-8 shadow-premium">
              {/* Consultation Preview */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-2 mb-6">
                <div className="flex items-center text-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center">
                    <Icon name="User" size={24} color="white" strokeWidth={2} />
                  </div>
                  <div>
                    <div className="text-white font-semibold">
                      Premium Consultation
                    </div>
                    <div className="text-white/70 text-sm">
                      30-minute strategy session
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-3 text-white/90">
                    <Icon
                      name="Check"
                      size={16}
                      color="#D4AF37"
                      strokeWidth={2}
                    />
                    <span className="text-sm">Project scope assessment</span>
                  </div>
                  <div className="flex items-center space-x-3 text-white/90">
                    <Icon
                      name="Check"
                      size={16}
                      color="#D4AF37"
                      strokeWidth={2}
                    />
                    <span className="text-sm">Creative vision alignment</span>
                  </div>
                  <div className="flex items-center space-x-3 text-white/90">
                    <Icon
                      name="Check"
                      size={16}
                      color="#D4AF37"
                      strokeWidth={2}
                    />
                    <span className="text-sm">
                      Technical requirements review
                    </span>
                  </div>
                  <div className="flex items-center space-x-3 text-white/90">
                    <Icon
                      name="Check"
                      size={16}
                      color="#D4AF37"
                      strokeWidth={2}
                    />
                    <span className="text-sm">Custom proposal delivery</span>
                  </div>
                </div>
              </div>

              {/* Availability Indicator */}
              <div className="bg-accent/20 backdrop-blur-sm rounded-xl p-2 text-center">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-white font-semibold">Available This Week</span>
                </div>
                <p className="text-white/80 text-sm">
                  Slots expire in: <span className="font-semibold">{timeLeft}</span>
                </p>
              </div>
            </div>

            {/* Floating Elements */}
            <div className="absolute -top-4 -right-4 w-16 h-16 bg-accent rounded-full flex items-center justify-center shadow-elevation">
              <Icon name="Star" size={24} color="white" strokeWidth={2} />
            </div>

            <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-elevation">
              <Icon
                name="Camera"
                size={28}
                color="var(--color-accent)"
                strokeWidth={1.5}
              />
            </div>
          </div>

          <div className="space-y-8">
            <div>

              <p className="text-lg sm:text-xl text-text-secondary mb-2 sm:mb-4 leading-relaxed">
                Boost your corporate branding with professional cinematic
                production and strategic visual storytelling. Book a
                consultation now to elevate brand awareness, engage your target
                audience, and drive business growth through expert video
                marketing.
              </p>
            </div>

            {/* Benefits Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="text-center p-4 bg-white rounded-lg shadow-subtle hover:shadow-elevation cinematic-transition hover-lift"
                >
                  <div className="w-12 h-12 bg-accent/10 rounded-full hidden sm:flex items-center justify-center mx-auto mb-3">
                    <Icon
                      name={benefit.icon}
                      size={24}
                      color="var(--color-accent)"
                      strokeWidth={2}
                    />
                  </div>
                  <h3 className="font-semibold text-red-700 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-text-secondary text-sm">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-3  gap-2">
              <Link
                to="/booking-concierge-premium-consultation"
                className="col-span-1 sm:col-span-2 inline-flex items-center justify-center space-x-2 bg-red-700 hover:bg-accent-dark border-2 border-red-700 text-white px-4 py-2 sm:py-4 text-sm uppercase rounded-lg font-semibold cinematic-transition cta-glow hover:shadow-elevation w-full"

                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <Icon name="Calendar" size={20} strokeWidth={2} />
                <span>Schedule Consultation</span>
                <Icon
                  name="ArrowRight"
                  size={16}
                  strokeWidth={2}
                  className={`cinematic-transition ${isHovered ? "translate-x-10" : ""
                    }`}
                />
              </Link>

              <Link
                to="/cinematic-showcase-portfolio"
                className="col-span-1 sm:col-span-1 inline-flex items-center justify-center space-x-2 border-2 border-red-700 text-red-700 hover:bg-red-700 hover:text-white px-4 py-2 sm:py-4 rounded-lg font-semibold cinematic-transition w-full"
              >
                <Icon name="Play" size={20} strokeWidth={2} />
                <span>View Portfolio</span>
              </Link>
            </div>

            {/* Contact Info */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-8 space-y-4 sm:space-y-0 pt-6 border-t border-border">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center">
                  <Icon
                    name="Phone"
                    size={18}
                    color="var(--color-accent)"
                    strokeWidth={2}
                  />
                </div>
                <div>
                  <div className="text-sm text-text-secondary">Call Direct</div>
                  <div className="font-semibold text-primary">
                    <a href="tel:+254740000444" className="hover:underline">
                      +254 (740) 000 444
                    </a>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center">
                  <Icon
                    name="Mail"
                    size={18}
                    color="var(--color-accent)"
                    strokeWidth={2}
                  />
                </div>
                <div>
                  <div className="text-sm text-text-secondary">Email Us</div>
                  <div className="font-semibold text-primary transition-all duration-300 ">
                    <a href="mailto:info@hype.co.ke" className="hover:underline">
                      info@hype.co.ke
                    </a>
                  </div>
                </div>

              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default BookingCTA;
